import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, ExternalLinkIcon, MoveIcon, PackageIcon, PhoneIcon, RulerIcon, SunIcon } from 'lucide-react';
import { business, interiorHeroes, routes } from '../data/site';
const hero = interiorHeroes.umbrellas;
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Patio Umbrellas in Brooklyn, MI | Pegasus Spas';
const DESCRIPTION = 'Compare patio umbrella styles for Michigan patios, then visit Pegasus Spas in Brooklyn, Michigan to review canopy, frame and operation options.';
const CANONICAL = 'https://www.pegasus-spas.com/39.html';
const MANUFACTURER_URL = 'https://eastcoastumbrella.com/';
const considerations = [{
  icon: RulerIcon,
  title: 'Area to Shade',
  copy: 'How much of the patio or seating area do you want covered?'
}, {
  icon: SunIcon,
  title: 'Table or Freestanding Use',
  copy: 'Will it go through a table opening, or stand on its own beside the seating?'
}, {
  icon: MoveIcon,
  title: 'Opening & Tilt',
  copy: 'How do you prefer to open it, and does the canopy need to tilt through the day?'
}, {
  icon: PackageIcon,
  title: 'Storage and Seasonal Care',
  copy: 'Where will the umbrella and its base go when the season ends?'
}];
const categories = [{
  title: 'Fiberglass Umbrellas',
  copy: 'Frames using fiberglass ribs, which are designed to flex rather than hold rigid.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/Umbrellas/newimages/EC75FPOP-MKT---7.5-ft.-Aluminum-Market-Popup-With-Fiberglass-Ribs-and-No-Tilt.png?opt=5',
  alt: 'Patio umbrella with an aluminum pole and fiberglass ribs'
}, {
  title: 'Wooden Umbrellas',
  copy: 'A traditional wood pole and rib look for patios and garden settings.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/Umbrellas/wooden/BJ1006-SQU--6.5-ft.-Wooden-Market-With-Pulley-and-No-Tilt.png?opt=5',
  alt: 'Square wooden market patio umbrella with a pulley lift'
}, {
  title: 'Heavy Duty Aluminum Umbrellas',
  copy: 'Heavier aluminum framing intended for more demanding everyday use.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/Umbrellas/heavy_duty_aluminum/heavy.png?opt=5',
  alt: 'Heavy duty aluminum patio umbrella with a reinforced frame'
}, {
  title: 'Standard Aluminum Umbrellas',
  copy: 'Familiar aluminum patio umbrellas with common crank and tilt operation.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/Umbrellas/Standard_Aluminum/1249CT---8.5-ft.-Aluminum-Standard-With-Crank-and-Tilt.png?opt=5',
  alt: 'Standard aluminum patio umbrella with a crank lift and tilt'
}, {
  title: 'Commercial Beach Umbrellas',
  copy: 'Styles built around commercial and waterfront use rather than a home patio table.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/Umbrellas/Commercial_Beach/639AT-LG---6.5-ft.-Aluminum-Pop-up-With-Tilt-and-3-Life-Guard-Logos.png?opt=5',
  alt: 'Commercial beach umbrella with an aluminum pop-up frame and tilt'
}, {
  title: 'Residential Umbrellas',
  copy: 'Home patio umbrellas covering everyday table and seating arrangements.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/Umbrellas/Residential/EC11FAUTO---11-ft.-Aluminum-Market-Crank-With-Fiberglass-Ribs-and-Auto-Tilt.png?opt=5',
  alt: 'Large residential market patio umbrella with a crank lift and auto tilt'
}];
const measurements = ['Width and depth of the area you want shaded', 'Table opening if the umbrella will pass through one', 'Desired seating coverage', 'Nearby walls, rooflines and branches', 'Where the umbrella and base can be stored'];
const relatedLinks = [{
  label: 'Fire Tables',
  href: routes.fireTables
}, {
  label: 'Big Green Egg',
  href: routes.grills
}, {
  label: 'Current Specials',
  href: routes.specials
}, {
  label: 'Visit the Showroom',
  href: routes.location
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function UmbrellasPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-2345" data-mp="2ue11nc5e24sk">
      {/* Intro band */}
      <section aria-labelledby="umbrellas-h1" className="relative w-full" data-id="element-2346" data-mp="2uf2cpgmemraa">
        <div className={`relative w-full overflow-hidden ${hero.frameClass}`} data-id="element-2347" data-mp="19wi2dx44oo1b">
          <img src={hero.src} alt={hero.alt} className={hero.imageClass} width={hero.width} height={hero.height} decoding="async" data-id="element-2348" data-mp="1eujl2gxn6lc9" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-2349" data-mp="153f8fnvfvckl" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-2350" data-mp="r3nxjw07ycat" />

          <div className={`relative mx-auto flex max-w-container flex-col justify-center px-5 py-10 sm:px-8 ${hero.contentClass}`} data-id="element-2351" data-mp="w425zf5qfo9d">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-2352" data-mp="i6dl47bo0h4b">
              Outdoor Shade
            </p>
            <h1 id="umbrellas-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-2353" data-mp="2chin7773slhj">
              Patio Umbrellas for Shade That Fits the Space
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-2354" data-mp="3phvh34kfrjfd">
              The right umbrella starts with the space beneath it: the table or seating area, the
              amount of shade needed, the opening method and the conditions around the patio.
              We can help you compare the options for a practical fit.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2355" data-mp="c0mh323ks9i1">
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2356" data-mp="80cbvrnlxn48">
                Visit the Showroom
              </SiteLink>
              <SiteLink to={routes.furniture} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2357" data-mp="vqq5vnpr5w1g">
                Explore Outdoor Furniture
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Start with the patio */}
      <section aria-labelledby="start-heading" className="w-full bg-white" data-id="element-2358" data-mp="1c9w017hj29ga">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2359" data-mp="1c7rjnq2fzhz4">
          <h2 id="start-heading" className="max-w-3xl text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2360" data-mp="1h8flz7w4fl8n">
            Start With the Patio, Not Just the Canopy
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2361" data-mp="1r0v3k43ia7t0">
            {considerations.map(item => {
            const Icon = item.icon;
            return <li key={item.title} data-id="element-2362" data-mp="1zxdlyme42dbc">
                  <Icon className="h-6 w-6 text-pine" aria-hidden="true" data-id="element-2363" data-mp="24y03dmcmmaft" />
                  <h3 className="mt-3 text-[19px] font-semibold leading-7 text-ink" data-id="element-2364" data-mp="2q9hv69qvmexf">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-2365" data-mp="2l9dgmp9j3u9u">{item.copy}</p>
                </li>;
          })}
          </ul>
        </div>
      </section>

      {/* Category gallery */}
      <section aria-labelledby="styles-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2366" data-mp="2v9w3lswe3qvz">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2367" data-mp="27gw5k76kbkla">
          <h2 id="styles-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2368" data-mp="22lgt9jo7dwqz">
            Compare Umbrella Styles
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2369" data-mp="2chbhqvf3ym1o">
            {categories.map(category => <li key={category.title} className="flex h-full flex-col rounded-card border border-ink/10 bg-white" data-id="element-2370" data-mp="1oqeiai4i1h7b">
                <div className="aspect-[4/3] w-full bg-water" data-id="element-2371" data-mp="3vfzmjo06d4jq">
                  <img src={category.image} alt={category.alt} className="h-full w-full object-contain p-4" loading="lazy" decoding="async" data-id="element-2372" data-mp="41ty1qqo4p8o" />
                </div>
                <div className="flex flex-1 flex-col p-5" data-id="element-2373" data-mp="i1v7vvowv70a">
                  <h3 className="text-h3 font-semibold text-ink" data-id="element-2374" data-mp="i2eewhd0wazj">{category.title}</h3>
                  <p className="mt-2 text-[16px] leading-6 text-ink/75" data-id="element-2375" data-mp="mzwn25j7um6h">{category.copy}</p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Measurements */}
      <section aria-labelledby="measurements-heading" className="w-full bg-water" data-id="element-2376" data-mp="d1756crbl1q8">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2377" data-mp="2tqcqrxakm422">
          <div className="max-w-3xl" data-id="element-2378" data-mp="2xtitww2dzf4b">
            <h2 id="measurements-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2379" data-mp="2xukbzkhtl1vj">
              Not Sure What Fits? Start With a Few Photos.
            </h2>

            <ul className="mt-6 space-y-2 border-t border-ink/15 pt-5" data-id="element-2380" data-mp="2esrzzdq170on">
              {measurements.map(item => <li key={item} className="text-[16.5px] leading-6 text-ink/80" data-id="element-2381" data-mp="250vuvipj2aax">
                  {item}
                </li>)}
            </ul>

            <p className="mt-6 text-[16px] leading-6 text-ink/70" data-id="element-2382" data-mp="1r01p77tv6nfy">
              Placement, base requirements and use in changing weather should follow the selected
              manufacturer's guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Showroom split */}
      <section aria-labelledby="compare-local-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2383" data-mp="1m2jog4q03ej1">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2384" data-mp="3ggargldtr05i">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-2385" data-mp="3ge6pwa3eed8e">
            <div className="lg:col-span-6" data-id="element-2386" data-mp="14t7dvo07yvkk">
              <h2 id="compare-local-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2387" data-mp="10rvsjuodowi6">
                See the Umbrellas in Person
              </h2>
              <p className="mt-4 text-body font-semibold text-ink sm:text-body-lg" data-id="element-2388" data-mp="19r8t1xuiyaqr">
                We keep standard umbrellas in stock. Colors and sizes vary, and custom orders are
                available.
              </p>
              <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-2389" data-mp="1jhk6cb03lwab">
                Stop in to open a few and compare canopy, frame and tilt side by side, or send us a
                photo of the patio and we&rsquo;ll suggest a starting point.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2390" data-mp="34n53u4thddj">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-2391" data-mp="3uaeq6f3qlzth">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-2392" data-mp="28567jkm3e2ee" />
                  Location &amp; Hours
                </SiteLink>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn border border-ink/25 px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2393" data-mp="2c7b16jcjiz11">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2394" data-mp="2qwgy20nwod5r" />
                  Call or Text {business.phone}
                </a>
              </div>

              <p className="mt-5 text-[16px] leading-6 text-ink/70" data-id="element-2395" data-mp="2zw41st43xv1z">
                Browse the manufacturer&rsquo;s full line:{' '}
                <a href={MANUFACTURER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2396" data-mp="34w8iaj50mps7">
                  Visit East Coast Umbrella
                  <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-2397" data-mp="faf2jrpqhvc1" />
                </a>
              </p>
            </div>

            <div className="lg:col-span-6" data-id="element-2398" data-mp="17w7x5e0vm3nq">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card bg-white" data-id="element-2399" data-mp="17x9hk93s82i4">
                <img src="https://eastcoastumbrella.com/wp-content/uploads/HD11FAUTO.png" alt="Magenta East Coast Umbrella market umbrella with a crank lift" className="h-full w-full object-contain p-6" loading="lazy" decoding="async" data-id="element-2400" data-mp="134gfxu3m1aa9" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navy CTA */}
      <section aria-labelledby="shade-plan-heading" className="w-full bg-ink" data-id="element-2401" data-mp="1ff63wh6m5f6s">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2402" data-mp="1fd36hc34sjs1">
          <div className="max-w-3xl" data-id="element-2403" data-mp="11gzg5tvdovku">
            <h2 id="shade-plan-heading" className="text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-2404" data-mp="16ehllaa6sso3">
              Make Shade Part of the Backyard Plan
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-2405" data-mp="1y5x8l5fnoheu">
              Shade works best when it is planned alongside the furniture, the dining area and the
              way the space is used through the afternoon.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2406" data-mp="1pb0rzrtlw2gy">
            <SiteLink to={routes.furniture} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2407" data-mp="2d1d8hr2x37j5">
              Explore Outdoor Furniture
            </SiteLink>
            <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white/70 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2408" data-mp="2n521by0v7z8a">
              Contact Us
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-2409" data-mp="24ad9md24jlb2">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2410" data-mp="2i5p3yjcdb9rg">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-2411" data-mp="2d7xerx33y1qy">
            {relatedLinks.map(link => <li key={link.label} data-id="element-2412" data-mp="3onue8f8ko42f">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2413" data-mp="3ooneqygxtxc4">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2414" data-mp="ejgnuok5nrqi" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}